import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { api } from '../api/client.js'
import { useSession } from '../api/session.js'
import type { ObjectDetail, ObjectNode, Template } from '../api/types.js'
import { Icon } from '../components/icons.js'
import { AssigneePicker, type Assignment } from '../components/people.js'
import { useToast } from '../components/toast.js'
import { addDays, startOfWeek, todayIso } from '../lib/format.js'
import { PageHead } from './Layout.js'
import { SearchResults, Tree } from './Objects.js'
import { AdhocModal } from './TaskDialogs.js'
import { TaskImportModal } from './TaskImport.js'
import {
  ColumnPicker,
  GROUP_LABEL,
  HINTS,
  TASK_COLUMNS,
  TaskTable,
  exportUrl,
  useColumns,
  type Dir,
  type GroupBy,
  type ListSummary,
  type SortKey,
} from './TaskTable.js'

const STATUS_FILTERS: { value: string; label: string }[] = [
  { value: '', label: 'Alle Status' },
  { value: 'open,missed', label: 'Offen und überfällig' },
  { value: 'missed', label: 'Überfällig' },
  { value: 'open', label: 'Offen' },
  { value: 'done', label: 'Erledigt' },
  { value: 'skipped', label: 'Entfällt' },
  { value: 'final_missed', label: 'Endgültig nicht erledigt' },
]

function weekOf(date: string) {
  const from = startOfWeek(date)
  return { from, to: addDays(from, 6) }
}

/**
 * Die Aufgabenliste, so wie die Disposition morgens arbeitet (Workshop 23.10.):
 * ein Zeitraum, meist eine Woche, die Spalten aus dem Hausmanager, der
 * Objektbaum als Filter, und alles, was Aufmerksamkeit braucht, auf einen
 * Blick. Mehrfachauswahl bis "alle im Filter"; Export genau dieser Liste.
 *
 * Every filter lives in the address, so a click into a plan and back lands on
 * the same list, page and scroll position.
 */
/** Where the search looks; "Müll" in Leistung does not find the Müllerstraße. */
const SEARCH_SCOPES = [
  { value: '', label: 'Überall', placeholder: 'Suchen: Leistung, Adresse, Person, Notiz, ID' },
  { value: 'task', label: 'Leistung', placeholder: 'Leistung suchen, z. B. Müll' },
  { value: 'place', label: 'Objekt und Adresse', placeholder: 'Objekt, Straße, Ort oder Wohnung suchen' },
  { value: 'person', label: 'Person', placeholder: 'Mitarbeiter oder Gruppe suchen' },
  { value: 'text', label: 'Notiz und Ergebnis', placeholder: 'Notiz, Mangel, Ergebnis oder ID suchen' },
]

export function TasksPage() {
  const queryClient = useQueryClient()
  const today = todayIso()
  const toast = useToast()
  const session = useSession()
  const [params, setParams] = useSearchParams()
  const week = weekOf(today)

  const from = params.get('von') ?? week.from
  const to = params.get('bis') ?? week.to
  const statuses = params.get('status') ?? ''
  const hint = params.get('hinweis') ?? ''
  const templateId = params.get('leistung') ?? ''
  const objectId = params.get('objekt') ?? ''
  const planId = params.get('plan') ?? ''
  const q = params.get('suche') ?? ''
  const qIn = SEARCH_SCOPES.some((o) => o.value === params.get('suchen_in')) ? params.get('suchen_in')! : ''
  const sortKey = (params.get('sort') as SortKey | null) ?? 'due'
  const dir = (params.get('richtung') as Dir | null) ?? 'asc'
  const page = Math.max(1, Number(params.get('seite') ?? '1') || 1)
  const groupBy = (['assignee', 'site'].includes(params.get('gruppieren') ?? '') ? params.get('gruppieren') : null) as GroupBy | null
  const openGroups = (params.get('auf') ?? '').split(',').filter(Boolean)
  const assignment: Assignment = { assigneeId: params.get('mitarbeiter'), assigneeTeamId: params.get('gruppe') }

  /** Changes filters in the address; any filter change starts at page one. */
  function update(changes: Record<string, string | null | undefined>, options: { keepPage?: boolean } = {}) {
    const next = new URLSearchParams(params)
    for (const [key, value] of Object.entries(changes)) {
      if (value) next.set(key, value)
      else next.delete(key)
    }
    if (!options.keepPage) next.delete('seite')
    setParams(next, { replace: true })
  }

  const [search, setSearch] = useState('')
  const [text, setText] = useState(q)
  // Typing should not fire a request per letter.
  useEffect(() => {
    const timer = setTimeout(() => {
      if (text.trim() !== q) update({ suche: text.trim() })
    }, 300)
    return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text])
  // "Filter zurücksetzen" empties the address; the search box follows.
  useEffect(() => {
    if (!q) setText('')
  }, [q])

  const [showAdhoc, setShowAdhoc] = useState(false)
  const [showImport, setShowImport] = useState(false)
  const [treeOpen, setTreeOpen] = useState(true)
  const [summary, setSummary] = useState<ListSummary | null>(null)
  const columns = useColumns(`hauswerk.taskColumns.${session.data?.user.id ?? 'anon'}`, TASK_COLUMNS)

  const filter = new URLSearchParams({ from, to })
  if (objectId) filter.set('objectId', objectId)
  if (assignment.assigneeId) filter.set('assigneeId', assignment.assigneeId)
  if (assignment.assigneeTeamId) filter.set('teamId', assignment.assigneeTeamId)
  if (statuses) filter.set('statuses', statuses)
  if (hint) filter.set('hint', hint)
  if (templateId) filter.set('templateId', templateId)
  if (planId) filter.set('planId', planId)
  if (q) filter.set('q', q)
  if (q && qIn) filter.set('qIn', qIn)

  const templates = useQuery({ queryKey: ['templates'], queryFn: () => api<Template[]>('/api/templates') })
  const node = useQuery({
    queryKey: ['object', objectId],
    queryFn: () => api<ObjectDetail>(`/api/objects/${objectId}`),
    enabled: Boolean(objectId),
  })

  function pickObject(picked: ObjectNode | null) {
    update({ objekt: picked?.id })
  }

  async function refresh() {
    await queryClient.invalidateQueries({ queryKey: ['occurrences'] })
  }

  // Anything that narrows the list; sort, page and grouping are views, not filters.
  const FILTER_KEYS = ['von', 'bis', 'status', 'hinweis', 'leistung', 'objekt', 'plan', 'suche', 'mitarbeiter', 'gruppe']
  const filtered = FILTER_KEYS.some((key) => params.has(key))
  function resetFilters() {
    const next = new URLSearchParams()
    for (const key of ['sort', 'richtung', 'gruppieren']) {
      const value = params.get(key)
      if (value) next.set(key, value)
    }
    setText('')
    setSearch('')
    setParams(next, { replace: true })
  }

  const lastWeek = weekOf(addDays(today, -7))
  const nextWeek = weekOf(addDays(today, 7))
  const quick: { label: string; from: string; to: string; status?: string }[] = [
    { label: 'Heute', from: today, to: today },
    { label: 'Gestern', from: addDays(today, -1), to: addDays(today, -1) },
    { label: 'Diese Woche', ...week },
    { label: 'Letzte Woche', ...lastWeek },
    { label: 'Nächste Woche', ...nextWeek },
    { label: 'Offen und überfällig', from: addDays(today, -30), to: today, status: 'open,missed' },
  ]
  const isQuick = (r: (typeof quick)[number]) => r.from === from && r.to === to && (r.status === undefined || r.status === statuses)
  const shift = (days: number) => update({ von: addDays(from, days), bis: addDays(to, days) })
  const span = Math.round((Date.parse(to) - Date.parse(from)) / 86_400_000) + 1

  return (
    <>
      <PageHead
        title="Aufgaben"
        subtitle="Alle Aufgaben im gewählten Zeitraum. Links nach Objekt filtern, oben nach Person, Status und Leistung."
        action={
          <div className="actions">
            <a className="btn secondary" href={exportUrl(filter, columns.shown, 'xlsx')} title="Genau diese Liste mit den sichtbaren Spalten">
              <Icon name="download" /> Als Excel
            </a>
            <a className="btn ghost" href={exportUrl(filter, columns.shown, 'csv')}>
              CSV
            </a>
            <button type="button" className="btn secondary" onClick={() => setShowImport(true)}>
              <Icon name="upload" /> Aus Excel importieren
            </button>
            <button type="button" className="btn" onClick={() => setShowAdhoc(true)}>
              <Icon name="plus" /> Sonderaufgabe
            </button>
          </div>
        }
      />

      <div className="explorer explorer-narrow">
        <aside className={`card tree ${treeOpen ? '' : 'collapsed'}`}>
          <div className="tree-search">
            <input type="search" placeholder="Objekt suchen" value={search} onChange={(e) => setSearch(e.target.value)} aria-label="Objekt suchen" />
          </div>
          <div className="card-body">
            <button type="button" className={`tree-all ${objectId ? '' : 'selected'}`} onClick={() => pickObject(null)}>
              Alle Objekte
            </button>
            {search.trim() ? (
              <SearchResults term={search.trim()} selectedId={objectId} onPick={pickObject} />
            ) : (
              <Tree selectedId={objectId} ancestors={node.data?.breadcrumb ?? []} onPick={pickObject} />
            )}
          </div>
        </aside>

        <div style={{ minWidth: 0 }}>
          <button type="button" className="btn secondary small tree-mobile-toggle" onClick={() => setTreeOpen((v) => !v)}>
            <Icon name="tree" size={16} /> {treeOpen ? 'Baum ausblenden' : 'Baum einblenden'}
          </button>

          <section className="card task-filters">
            <div className="task-filter-row">
              <div className="quick-range" role="group" aria-label="Zeitraum">
                {quick.map((r) => (
                  <button
                    key={r.label}
                    type="button"
                    className={`btn small ${isQuick(r) ? '' : 'secondary'}`}
                    aria-pressed={isQuick(r)}
                    onClick={() => update({ von: r.from, bis: r.to, ...(r.status !== undefined ? { status: r.status } : {}) })}
                  >
                    {r.label}
                  </button>
                ))}
              </div>
              <div className="range-step">
                <button type="button" className="btn ghost small" aria-label="Zeitraum zurück" onClick={() => shift(-span)}>
                  <Icon name="chevronLeft" size={16} />
                </button>
                <input
                  type="date"
                  value={from}
                  aria-label="Von"
                  onChange={(e) => e.target.value && update({ von: e.target.value, bis: to < e.target.value ? e.target.value : to })}
                />
                <span className="muted">bis</span>
                <input
                  type="date"
                  value={to}
                  aria-label="Bis"
                  onChange={(e) => e.target.value && update({ von: from > e.target.value ? e.target.value : from, bis: e.target.value })}
                />
                <button type="button" className="btn ghost small" aria-label="Zeitraum vor" onClick={() => shift(span)}>
                  <Icon name="chevronRight" size={16} />
                </button>
              </div>
            </div>
            <div className="task-filter-row">
              <div className="task-search-group">
                <select value={qIn} onChange={(e) => update({ suchen_in: e.target.value })} aria-label="Suchen in">
                  {SEARCH_SCOPES.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
                <input
                  type="search"
                  className="task-search"
                  placeholder={SEARCH_SCOPES.find((o) => o.value === qIn)!.placeholder}
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  aria-label="Aufgaben durchsuchen"
                />
              </div>
              <div className="task-filter-person">
                <AssigneePicker
                  value={assignment}
                  onChange={(a) => update({ mitarbeiter: a.assigneeId, gruppe: a.assigneeTeamId })}
                  dense
                  emptyLabel="Alle Personen"
                  placeholder="Person oder Gruppe"
                />
              </div>
              <select value={statuses} onChange={(e) => update({ status: e.target.value })} aria-label="Status">
                {STATUS_FILTERS.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </select>
              <select value={hint} onChange={(e) => update({ hinweis: e.target.value })} aria-label="Hinweis">
                <option value="">Alle Hinweise</option>
                {HINTS.map((h) => (
                  <option key={h.value} value={h.value}>
                    {h.label}
                  </option>
                ))}
              </select>
              <select value={templateId} onChange={(e) => update({ leistung: e.target.value })} aria-label="Leistung">
                <option value="">Alle Leistungen</option>
                {templates.data?.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
              <label className="inline-field">
                <span className="muted small">Gruppieren</span>
                <select value={groupBy ?? ''} onChange={(e) => update({ gruppieren: e.target.value, auf: null })} aria-label="Gruppieren">
                  <option value="">ohne</option>
                  <option value="assignee">nach {GROUP_LABEL.assignee}</option>
                  <option value="site">nach {GROUP_LABEL.site}</option>
                </select>
              </label>
              <ColumnPicker columns={columns} />
            </div>
            <div className="task-summary small">
              <span className="strong">{summary?.total ?? 0} Aufgaben</span>
              {node.data && (
                <button type="button" className="chip chip-btn" onClick={() => pickObject(null)} title="Objektfilter entfernen">
                  {node.data.name} ✕
                </button>
              )}
              {planId && (
                <button type="button" className="chip chip-btn" onClick={() => update({ plan: null })} title="Planfilter entfernen">
                  Aufgaben eines Plans{summary?.firstTitle ? `: ${summary.firstTitle}` : ''} ✕
                </button>
              )}
              {summary && (
                <span className="muted">
                  {summary.missed > 0 && <span className="text-bad">{summary.missed} überfällig</span>}
                  {summary.missed > 0 && ', '}
                  {summary.open} offen, {summary.done} erledigt
                  {summary.pageOnly ? ' auf dieser Seite' : ''}
                </span>
              )}
              {summary?.fetching && <span className="muted">wird geladen …</span>}
              {filtered && (
                <button type="button" className="btn ghost small reset-filters" onClick={resetFilters}>
                  <Icon name="close" size={14} /> Alle Filter zurücksetzen
                </button>
              )}
            </div>
          </section>

          <section className="card">
            <TaskTable
              filter={filter}
              columns={columns.shown}
              sortKey={sortKey}
              dir={dir}
              onSort={(key, d) => update({ sort: key, richtung: d })}
              page={page}
              onPage={(p) => update({ seite: String(p) }, { keepPage: true })}
              groupBy={groupBy}
              openGroups={openGroups}
              onOpenGroups={(keys) => update({ auf: keys.join(',') }, { keepPage: true })}
              onSummary={setSummary}
              emptyHint={q ? `Nichts gefunden zu „${q}“. Suchbegriff, Zeitraum oder Filter anpassen.` : 'Zeitraum oder Filter anpassen.'}
              scrollKey={`hauswerk.tasksScroll.${params.toString()}`}
            />
          </section>
        </div>
      </div>

      <AdhocModal
        open={showAdhoc}
        defaultObjectId={objectId || undefined}
        onClose={() => setShowAdhoc(false)}
        onCreated={() => {
          setShowAdhoc(false)
          void refresh()
        }}
      />
      <TaskImportModal
        open={showImport}
        onClose={() => setShowImport(false)}
        onImported={(created) => {
          toast(`${created} Aufgaben aus der Liste angelegt.`)
          void refresh()
        }}
      />
    </>
  )
}
